import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import CtaBand from "@/components/CtaBand";
import Seo from "@/components/Seo";

export default function ServicesPage() {
  return (
    <>
      <Seo
        title="Boat Modifications & Aluminium Fabrication Services | SAMI FAB Albany"
        description="Explore SAMI FAB services in Albany WA: boat modifications, kit boat builds, boat maintenance, aluminium toolboxes, ute canopies, trailer fabrication and caravan accessories."
        keywords="boat modifications Albany WA, aluminium fabrication services, kit boat builds, boat maintenance Albany, aluminium toolboxes, ute canopies, trailer fabrication, caravan accessories"
        path="/services"
      />
      {/* Page header */}
      <section data-testid="services-page-header" className="relative overflow-hidden pt-[76px] bg-white border-b border-slate-200">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
          <div className="overflow-hidden">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]"
            >
              Services — Sami Fab, Albany WA
            </motion.p>
          </div>
          <div className="overflow-hidden mt-4">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
              className="font-display font-black uppercase tracking-tighter text-[#003366] text-4xl sm:text-5xl lg:text-6xl leading-[0.98]"
            >
              Every service.<br />Done properly.
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
          >
            From our most popular boat modifications to toolboxes, canopies, trays, trailers and
            caravan gear — here’s exactly what we can do for you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-wrap gap-2.5"
            data-testid="services-quick-jump"
          >
            {SERVICES.map((s) => (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                data-testid={`jump-${s.slug}`}
                className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] border border-slate-300 text-slate-600 px-3.5 py-2 hover:border-[#007bff] hover:text-[#007bff] transition-colors duration-200"
              >
                {s.title}
              </a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Detail sections */}
      {SERVICES.map((s, i) => (
        <motion.section
          key={s.slug}
          id={s.slug}
          data-testid={`service-detail-${s.slug}`}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className={`scroll-mt-28 py-16 lg:py-24 border-b border-[#007bff]/15 ${i % 2 === 1 ? "bg-[#F8F9FA]" : "bg-white"}`}
        >
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="group relative border border-slate-300 overflow-hidden aspect-[4/3]">
                <img
                  src={s.image}
                  alt={`${s.title} — aluminium fabrication by SAMI FAB in Albany WA`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#003366] text-white px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em]">
                  {s.tag}
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="font-display font-black text-5xl lg:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-[#007bff] to-[#003366] leading-none">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="font-display font-black uppercase tracking-tight text-3xl lg:text-4xl text-[#003366] mt-4">
                {s.title}
              </h2>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl">{s.detail}</p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check size={16} strokeWidth={3} className="text-[#007bff] mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/quote"
                  data-testid={`service-quote-cta-${s.slug}`}
                  className="group inline-flex items-center gap-3 bg-[#343a40] text-white px-7 py-3.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
                >
                  Get a quote for this
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                {s.slug === "kit-boat-builds" && (
                  <Link
                    to="/gallery?category=kit-boat-builds"
                    data-testid="service-process-cta-kit-boat-builds"
                    className="group inline-flex items-center gap-3 border border-[#007bff]/45 bg-white text-[#003366] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.15em] hover:border-[#007bff] hover:bg-[#007bff] hover:text-white hover:scale-[1.03] transition-[color,background-color,border-color,transform] duration-200"
                  >
                    See Process
                    <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </motion.section>
      ))}

      <section data-testid="services-custom-cta" className="py-16 lg:py-24 border-b border-[#007bff]/15 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-display font-black text-5xl lg:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-[#007bff] to-[#003366] leading-none">
              07
            </p>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl lg:text-4xl text-[#003366] mt-4">
              Got Something Else In Mind?
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl">
              If it’s aluminium, chances are we can build it. One-off custom jobs are our bread and
              butter, so tell us what you’re dreaming up and we’ll make it happen.
            </p>
            <Link
              to="/quote"
              data-testid="services-custom-quote-button"
              className="group mt-9 inline-flex items-center gap-3 bg-[#343a40] text-white px-7 py-3.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
            >
              Free Quote
              <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
